import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const AchievementDetailScreen = ({ route, navigation }) => {
  const { achievementId } = route.params || {};
  const isDarkMode = useColorScheme() === 'dark';

  // 模拟成就数据 - 实际应用中应该根据achievementId获取数据
  const achievement = {
    id: achievementId,
    title: '连续打卡30天',
    description: '连续30天记录健康数据',
    longDescription: '每天坚持记录健康数据，形成良好习惯，是健康管理的第一步。连续30天的坚持，证明了您对健康管理的决心和毅力！',
    category: 'activity',
    image: 'https://via.placeholder.com/300',
    points: 100,
    rarity: 'uncommon',
    dateCompleted: '2023年4月15日',
    steps: [
      '每天在APP中记录至少一项健康数据',
      '不中断，连续记录30天',
      '系统自动跟踪您的记录情况',
    ],
    benefits: [
      '获得100积分奖励',
      '解锁"坚持不懈"徽章',
      '提高个人健康档案完整度',
    ],
    progress: 100,
    statistics: {
      totalUsers: 10000,
      completionRate: '32%',
      averageDays: 18,
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return '#78909c';
      case 'uncommon': return '#4caf50';
      case 'rare': return '#2196f3';
      case 'epic': return '#9c27b0';
      case 'legendary': return '#ff9800';
      default: return '#78909c';
    }
  };

  const getRarityText = (rarity) => {
    switch (rarity) {
      case 'common': return '普通';
      case 'uncommon': return '少见';
      case 'rare': return '稀有';
      case 'epic': return '史诗';
      case 'legendary': return '传说';
      default: return '普通';
    }
  };

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#1c1c1c' : '#f5f5f5',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 成就头部 */}
      <View style={[
        styles.card,
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}>
        <Image 
          source={{ uri: achievement.image }} 
          style={styles.achievementImage} 
          resizeMode="cover"
        />
        
        <View style={styles.achievementHeader}>
          <Text style={[
            styles.achievementTitle,
            isDarkMode && styles.textLight
          ]}>
            {achievement.title}
          </Text>
          
          <View style={[
            styles.rarityBadge, 
            { backgroundColor: getRarityColor(achievement.rarity) }
          ]}>
            <Text style={styles.rarityText}>
              {getRarityText(achievement.rarity)}
            </Text>
          </View>
        </View>
        
        <Text style={[
          styles.achievementDescription,
          isDarkMode && styles.textLightSecondary
        ]}>
          {achievement.longDescription || achievement.description}
        </Text>

        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, isDarkMode && styles.textLight]}>
              {achievement.points}
            </Text>
            <Text style={[styles.statLabel, isDarkMode && styles.textLightSecondary]}>
              积分奖励
            </Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={[styles.statValue, isDarkMode && styles.textLight]}>
              {achievement.dateCompleted || '未获得'}
            </Text>
            <Text style={[styles.statLabel, isDarkMode && styles.textLightSecondary]}>
              获得日期
            </Text>
          </View>
        </View>
      </View>

      {/* 获取步骤 */}
      <View style={[
        styles.card,
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}>
        <Text style={[styles.sectionTitle, isDarkMode && styles.textLight]}>
          获取方式
        </Text>
        
        {achievement.steps.map((step, index) => (
          <View key={index} style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>{index + 1}</Text>
            </View>
            <Text style={[styles.stepText, isDarkMode && styles.textLight]}>
              {step}
            </Text>
          </View>
        ))}
      </View>

      {/* 奖励内容 */}
      <View style={[
        styles.card,
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}>
        <Text style={[styles.sectionTitle, isDarkMode && styles.textLight]}>
          奖励内容
        </Text>
        
        {achievement.benefits.map((benefit, index) => (
          <View key={index} style={styles.benefitItem}>
            <Ionicons 
              name="checkmark-circle" 
              size={24} 
              color={isDarkMode ? '#4caf50' : '#2e7d32'} 
            />
            <Text style={[styles.benefitText, isDarkMode && styles.textLight]}>
              {benefit}
            </Text>
          </View>
        ))}
      </View>

      {/* 成就统计 */}
      <View style={[
        styles.card,
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}>
        <Text style={[styles.sectionTitle, isDarkMode && styles.textLight]}>
          成就统计
        </Text>
        
        <View style={styles.statsGrid}>
          <View style={styles.statGridItem}>
            <Text style={[styles.statGridValue, isDarkMode && styles.textLight]}>
              {achievement.statistics.totalUsers.toLocaleString()}
            </Text>
            <Text style={[styles.statGridLabel, isDarkMode && styles.textLightSecondary]}>
              总参与人数
            </Text>
          </View>
          
          <View style={styles.statGridItem}>
            <Text style={[styles.statGridValue, isDarkMode && styles.textLight]}>
              {achievement.statistics.completionRate}
            </Text>
            <Text style={[styles.statGridLabel, isDarkMode && styles.textLightSecondary]}>
              完成率
            </Text>
          </View>
          
          <View style={styles.statGridItem}>
            <Text style={[styles.statGridValue, isDarkMode && styles.textLight]}>
              {achievement.statistics.averageDays}天
            </Text>
            <Text style={[styles.statGridLabel, isDarkMode && styles.textLightSecondary]}>
              平均完成时间
            </Text>
          </View>
        </View>
      </View>

      {/* 分享按钮 */}
      <TouchableOpacity
        style={[
          styles.shareButton,
          { backgroundColor: isDarkMode ? '#2196f3' : '#1976d2' }
        ]}
        onPress={() => alert('分享功能将在未来版本中推出')}
      >
        <Ionicons name="share-social" size={20} color="white" />
        <Text style={styles.shareButtonText}>分享此成就</Text>
      </TouchableOpacity>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardLight: {
    backgroundColor: '#fff',
  },
  cardDark: {
    backgroundColor: '#2a2a2a',
  },
  achievementImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    marginBottom: 16,
  },
  achievementHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  achievementTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  rarityBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  rarityText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 12,
  },
  achievementDescription: {
    fontSize: 16,
    color: '#666',
    lineHeight: 22,
    marginBottom: 16,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  statLabel: {
    fontSize: 12,
    color: '#777',
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#2196f3',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  stepText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  benefitText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 12,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statGridItem: {
    width: '30%',
    alignItems: 'center',
    marginBottom: 8,
  },
  statGridValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  statGridLabel: {
    fontSize: 12,
    color: '#777',
    marginTop: 4,
    textAlign: 'center',
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 8,
    marginVertical: 8,
  },
  shareButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  textLight: {
    color: '#f5f5f5',
  },
  textLightSecondary: {
    color: '#aaa',
  },
});

export default AchievementDetailScreen; 